import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, LoaderCircle, RefreshCw } from 'lucide-react';
import EmptyState from '../../../shared/components/ui/EmptyState';
import Skeleton from '../../../shared/components/ui/Skeleton';
import AiBadge from './AiBadge';
import AiOfferRecommendationCard from './AiOfferRecommendationCard';
import { formatRetryAfter, normalizeAiOfferRecommendationError } from '../api/aiOfferRecommendationsApi';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog';
import './AiOfferRecommendationPanel.css';

const pad2 = (value) => String(value).padStart(2, '0');

const formatDateTimeLocalValue = (date) => (
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`
);

const roundUpMinutes = (date, intervalMinutes = 15) => {
  const rounded = new Date(date.getTime());
  const intervalMs = intervalMinutes * 60 * 1000;
  return new Date(Math.ceil(rounded.getTime() / intervalMs) * intervalMs);
};

const withSeconds = (value) => {
  const text = String(value || '').trim();
  if (!text) {
    return null;
  }
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(text) ? `${text}:00` : text;
};

const getDefaultPublishWindow = () => {
  const start = roundUpMinutes(new Date(), 15);
  const end = new Date(start.getTime());
  end.setDate(end.getDate() + 14);
  return {
    from: formatDateTimeLocalValue(start),
    until: formatDateTimeLocalValue(end)
  };
};

const formatInr = (value) => new Intl.NumberFormat('en-IN').format(value);

const parseRuleInt = (value) => {
  const text = String(value ?? '').trim();
  if (!text) {
    return null;
  }
  const parsed = Number(text);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

// Plain-English summary of the entered rules, with a worked example, so the
// merchant sees exactly what their numbers do before saving.
const buildRulesSummary = (form) => {
  const maxPercent = parseRuleInt(form.maxDiscountPercent);
  const capAmount = parseRuleInt(form.maxPercentDiscountCapAmount);
  const maxFlat = parseRuleInt(form.maxFlatDiscount);
  const minBill = parseRuleInt(form.minBillAmount);
  const sentences = [];

  if (maxPercent && capAmount) {
    const thresholdBill = Math.ceil((capAmount / maxPercent) * 100);
    const exampleBill = Math.ceil(Math.max(thresholdBill * 2, thresholdBill + 500) / 500) * 500;
    const uncapped = Math.round((maxPercent / 100) * exampleBill);
    sentences.push(
      `Percentage offers can go up to ${maxPercent}% off, but never more than ₹${formatInr(capAmount)}. `
      + `Example: ${maxPercent}% of a ₹${formatInr(exampleBill)} bill is ₹${formatInr(uncapped)}, `
      + `but the customer only gets ₹${formatInr(capAmount)} off.`
    );
  } else if (maxPercent) {
    sentences.push(`Percentage offers can go up to ${maxPercent}% off (no ₹ cap).`);
  }

  if (maxFlat) {
    if (minBill) {
      sentences.push(
        `Flat offers can be up to ₹${formatInr(maxFlat)} off, and only apply on bills of `
        + `₹${formatInr(minBill)} or more (so no ₹${formatInr(maxFlat)} off a tiny sale).`
      );
    } else {
      sentences.push(`Flat offers can be up to ₹${formatInr(maxFlat)} off.`);
    }
  }

  if (sentences.length === 0) {
    return null;
  }
  sentences.push('The AI still picks the exact discount within these limits.');
  return sentences;
};

const GENERATION_STEPS = [
  'Reading services and active offers',
  'Checking aggregate analytics',
  'Drafting offer ideas',
  'Validating discounts and safety'
];

const AiOfferRecommendationPanel = ({
  recommendations = [],
  loading = false,
  isInitialLoading = false,
  isMutating = false,
  isGenerating = false,
  generationStatus,
  generationMessage,
  error,
  lastRateLimitMessage,
  generationQuota = null,
  onRefresh,
  onPublish,
  onDismiss,
  rules = null,
  rulesLoading = false,
  onFetchRules,
  onSaveRules,
  // Rules modal is opened from the page header (next to "Generate AI Offer");
  // the parent owns the open flag, this panel renders the modal + form.
  rulesOpen = false,
  onCloseRules
}) => {
  const [publishDraft, setPublishDraft] = React.useState(null);
  const [publishFrom, setPublishFrom] = React.useState('');
  const [publishUntil, setPublishUntil] = React.useState('');
  const [publishError, setPublishError] = React.useState('');
  const [duplicateConfirm, setDuplicateConfirm] = React.useState(null);
  const [rulesForm, setRulesForm] = React.useState({
    maxDiscountPercent: '',
    maxPercentDiscountCapAmount: '',
    maxFlatDiscount: '',
    minBillAmount: ''
  });
  const [rulesError, setRulesError] = React.useState('');
  const [rulesSaving, setRulesSaving] = React.useState(false);
  const rulesSeededRef = React.useRef(false);
  const visibleRecommendations = React.useMemo(() => (
    recommendations.filter((recommendation) => {
      const status = String(recommendation?.status || '').toUpperCase();
      // EXPIRED = superseded by a newer generation run.
      // PUBLISHED = already promoted to a live offer; it lives in the Campaigns
      // tab now, so drop it from AI Studio instead of showing it twice.
      return status !== 'REJECTED' && status !== 'EXPIRED' && status !== 'PUBLISHED';
    })
  ), [recommendations]);
  const hasRecommendations = visibleRecommendations.length > 0;
  const quotaExhausted = Boolean(generationQuota && (generationQuota.remaining ?? 1) <= 0);
  const quotaResetText = quotaExhausted && generationQuota?.retryAtMs
    ? formatRetryAfter(Math.round((generationQuota.retryAtMs - Date.now()) / 1000))
    : null;
  const quotaAlertMessage = quotaExhausted
    ? `AI offer generation limit reached.${quotaResetText ? ` You can generate a new set in ${quotaResetText}.` : ''}`
    : null;
  const generationAlertMessage = lastRateLimitMessage
    || quotaAlertMessage
    || (generationStatus === 'FAILED' ? generationMessage : null)
    || (generationStatus === 'IN_PROGRESS' && !isGenerating ? generationMessage : null);

  const openPublishModal = React.useCallback((recommendation) => {
    const defaults = getDefaultPublishWindow();
    setPublishDraft(recommendation);
    setPublishFrom(defaults.from);
    setPublishUntil(defaults.until);
    setPublishError('');
  }, []);

  const closePublishModal = React.useCallback(() => {
    setPublishDraft(null);
    setPublishFrom('');
    setPublishUntil('');
    setPublishError('');
    setDuplicateConfirm(null);
  }, []);

  const toFormValue = (value) => (value == null ? '' : String(value));

  // On open (parent-driven): reset the one-shot seed + refetch the rules.
  React.useEffect(() => {
    if (!rulesOpen) {
      return;
    }
    rulesSeededRef.current = false;
    setRulesError('');
    const maybePromise = onFetchRules?.();
    if (maybePromise && typeof maybePromise.catch === 'function') {
      maybePromise.catch(() => {
        // The hook keeps the last-known rules; the modal still seeds from them.
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesOpen]);

  // Seed the form once per open, as soon as (cached or freshly fetched) rules are available.
  React.useEffect(() => {
    if (!rulesOpen || rulesSeededRef.current) {
      return;
    }
    if (rules == null && rulesLoading) {
      return;
    }
    rulesSeededRef.current = true;
    setRulesForm({
      maxDiscountPercent: toFormValue(rules?.maxDiscountPercent),
      maxPercentDiscountCapAmount: toFormValue(rules?.maxPercentDiscountCapAmount),
      maxFlatDiscount: toFormValue(rules?.maxFlatDiscount),
      minBillAmount: toFormValue(rules?.minBillAmount)
    });
  }, [rulesOpen, rules, rulesLoading]);

  const closeRulesModal = React.useCallback(() => {
    setRulesError('');
    onCloseRules?.();
  }, [onCloseRules]);

  const updateRuleField = (field) => (event) => {
    const { value } = event.target;
    setRulesForm((previous) => ({ ...previous, [field]: value }));
    setRulesError('');
  };

  const confirmSaveRules = async () => {
    const toIntOrNull = (value) => {
      const text = String(value ?? '').trim();
      if (!text) {
        return null;
      }
      const parsed = Number(text);
      return Number.isInteger(parsed) ? parsed : NaN;
    };
    const parsed = {
      maxDiscountPercent: toIntOrNull(rulesForm.maxDiscountPercent),
      maxPercentDiscountCapAmount: toIntOrNull(rulesForm.maxPercentDiscountCapAmount),
      maxFlatDiscount: toIntOrNull(rulesForm.maxFlatDiscount),
      minBillAmount: toIntOrNull(rulesForm.minBillAmount)
    };
    if (Object.values(parsed).some((value) => Number.isNaN(value))) {
      setRulesError('Enter whole numbers only.');
      return;
    }
    if (parsed.maxDiscountPercent == null) {
      setRulesError('Max discount % is required.');
      return;
    }
    if (parsed.maxFlatDiscount == null) {
      setRulesError('Max flat discount is required.');
      return;
    }
    if (parsed.maxDiscountPercent < 1 || parsed.maxDiscountPercent > 100) {
      setRulesError('Max discount % must be between 1 and 100.');
      return;
    }
    if ([parsed.maxPercentDiscountCapAmount, parsed.maxFlatDiscount, parsed.minBillAmount]
      .some((value) => value != null && value < 1)) {
      setRulesError('Amounts must be positive.');
      return;
    }
    setRulesSaving(true);
    try {
      await onSaveRules?.(parsed);
      closeRulesModal();
    } catch {
      // The hook owns merchant-facing error toasts for API failures.
    } finally {
      setRulesSaving(false);
    }
  };

  React.useEffect(() => {
    if (typeof document === 'undefined' || (!publishDraft && !isGenerating)) {
      return undefined;
    }
    const body = document.body;
    body.classList.add('modal-open');

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closePublishModal();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      body.classList.remove('modal-open');
    };
  }, [closePublishModal, isGenerating, publishDraft]);

  React.useEffect(() => {
    if (typeof document === 'undefined' || !rulesOpen) {
      return undefined;
    }
    const body = document.body;
    body.classList.add('modal-open');
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeRulesModal();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      body.classList.remove('modal-open');
    };
  }, [closeRulesModal, rulesOpen]);

  const confirmPublish = async () => {
    if (!publishDraft?.id) {
      return;
    }
    if (!publishFrom || !publishUntil) {
      setPublishError('Select both start and end date/time.');
      return;
    }
    if (String(publishUntil).localeCompare(String(publishFrom)) <= 0) {
      setPublishError('End must be after start.');
      return;
    }

    try {
      await onPublish?.(publishDraft.id, {
        validityFrom: withSeconds(publishFrom),
        validityUntil: withSeconds(publishUntil)
      });
      closePublishModal();
    } catch (error) {
      // A "similar offer already exists" soft duplicate isn't a hard failure — ask the
      // merchant whether to publish anyway instead of just toasting an error.
      const details = normalizeAiOfferRecommendationError(error);
      if (details.code === 'OFFER_DUPLICATE_SIMILAR') {
        setDuplicateConfirm({ message: details.message });
        return;
      }
      // Other errors: the hook owns merchant-facing error toasts.
    }
  };

  const confirmForcePublish = async () => {
    if (!publishDraft?.id) {
      setDuplicateConfirm(null);
      return;
    }
    try {
      await onPublish?.(publishDraft.id, {
        validityFrom: withSeconds(publishFrom),
        validityUntil: withSeconds(publishUntil),
        forcePublish: true
      });
      closePublishModal();
    } catch {
      // The hook owns merchant-facing error toasts for API failures.
      setDuplicateConfirm(null);
    }
  };

  const dismissRecommendation = async (recommendation) => {
    if (!recommendation?.id) {
      return;
    }
    try {
      await onDismiss?.(recommendation.id, 'Dismissed by merchant');
    } catch {
      // The hook owns merchant-facing error toasts for API failures.
    }
  };

  const handleRefresh = React.useCallback(() => {
    const refresh = onRefresh?.();
    if (refresh && typeof refresh.catch === 'function') {
      refresh.catch(() => {
        // The hook owns merchant-facing error toasts for API failures.
      });
    }
  }, [onRefresh]);

  const publishModal = publishDraft && typeof document !== 'undefined'
    ? createPortal(
      <div
        className="zone-modal-overlay offers-builder-overlay"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            closePublishModal();
          }
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            closePublishModal();
          }
        }}
      >
        <div
          className="zone-modal ai-offer-publish-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Publish AI offer"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="zone-modal-header offers-builder-modal-header">
            <div>
              <h3>Publish AI offer</h3>
              <p>{publishDraft?.draft?.title || 'Smart offer'}</p>
            </div>
            <button className="btn ghost" type="button" onClick={closePublishModal}>
              Close
            </button>
          </div>

          <div className="ai-offer-publish-body">
            <div className="offers-form-grid">
              <div className="offers-field">
                <label htmlFor="ai-offer-publish-from">Start</label>
                <input
                  id="ai-offer-publish-from"
                  className="analytics-select"
                  type="datetime-local"
                  value={publishFrom}
                  onChange={(event) => {
                    setPublishFrom(event.target.value);
                    setPublishError('');
                  }}
                />
              </div>
              <div className="offers-field">
                <label htmlFor="ai-offer-publish-until">End</label>
                <input
                  id="ai-offer-publish-until"
                  className="analytics-select"
                  type="datetime-local"
                  value={publishUntil}
                  onChange={(event) => {
                    setPublishUntil(event.target.value);
                    setPublishError('');
                  }}
                />
              </div>
            </div>
            {publishError ? <p className="ai-offer-inline-error">{publishError}</p> : null}
            <div className="offers-composer-actions">
              <button className="btn ghost" type="button" onClick={closePublishModal}>
                Cancel
              </button>
              <button className="btn primary" type="button" onClick={confirmPublish} disabled={isMutating}>
                {isMutating ? 'Publishing...' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    )
    : null;

  const rulesSummaryBullets = buildRulesSummary(rulesForm);

  const rulesModal = rulesOpen && typeof document !== 'undefined'
    ? createPortal(
      <div
        className="zone-modal-overlay offers-builder-overlay"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            closeRulesModal();
          }
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            closeRulesModal();
          }
        }}
      >
        <div
          className="zone-modal ai-offer-publish-modal"
          role="dialog"
          aria-modal="true"
          aria-label="AI offer rules"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="zone-modal-header offers-builder-modal-header">
            <div>
              <h3>AI offer rules</h3>
              <p>Guardrails the AI must stay within when drafting offers.</p>
            </div>
            <button className="btn ghost" type="button" onClick={closeRulesModal}>
              Close
            </button>
          </div>

          <div className="ai-offer-publish-body">
            <div className="offers-form-grid">
              <div className="offers-field">
                <label htmlFor="ai-offer-rule-max-percent">
                  Max discount %<span className="ai-offer-required" aria-hidden="true"> *</span>
                </label>
                <input
                  id="ai-offer-rule-max-percent"
                  className="analytics-select"
                  type="number"
                  min="1"
                  max="100"
                  inputMode="numeric"
                  placeholder="e.g. 5"
                  value={rulesForm.maxDiscountPercent}
                  onChange={updateRuleField('maxDiscountPercent')}
                />
              </div>
              <div className="offers-field">
                <label htmlFor="ai-offer-rule-cap-amount">% discount cap (₹ up to)</label>
                <input
                  id="ai-offer-rule-cap-amount"
                  className="analytics-select"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  placeholder="e.g. 150"
                  value={rulesForm.maxPercentDiscountCapAmount}
                  onChange={updateRuleField('maxPercentDiscountCapAmount')}
                />
              </div>
              <div className="offers-field">
                <label htmlFor="ai-offer-rule-max-flat">
                  Max flat discount (₹)<span className="ai-offer-required" aria-hidden="true"> *</span>
                </label>
                <input
                  id="ai-offer-rule-max-flat"
                  className="analytics-select"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  placeholder="e.g. 150"
                  value={rulesForm.maxFlatDiscount}
                  onChange={updateRuleField('maxFlatDiscount')}
                />
              </div>
              <div className="offers-field">
                <label htmlFor="ai-offer-rule-min-bill">Min bill for flat offers (₹)</label>
                <input
                  id="ai-offer-rule-min-bill"
                  className="analytics-select"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  placeholder="Optional"
                  value={rulesForm.minBillAmount}
                  onChange={updateRuleField('minBillAmount')}
                />
              </div>
            </div>
            {rulesSummaryBullets ? (
              <ul className="ai-offer-rules-hint ai-offer-rules-hint-list">
                {rulesSummaryBullets.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : (
              <p className="ai-offer-rules-hint">
                Percentage offers are capped by the max % and the ₹ cap. Flat offers are capped by the
                max ₹ and must require the minimum bill. The AI still chooses the exact discount within these limits.
              </p>
            )}
            {rulesError ? <p className="ai-offer-inline-error">{rulesError}</p> : null}
            <div className="offers-composer-actions">
              <button className="btn ghost" type="button" onClick={closeRulesModal}>
                Cancel
              </button>
              <button className="btn primary" type="button" onClick={confirmSaveRules} disabled={rulesSaving}>
                {rulesSaving ? 'Saving...' : 'Save rules'}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    )
    : null;

  const generationModal = isGenerating && typeof document !== 'undefined'
    ? createPortal(
      <div className="zone-modal-overlay offers-builder-overlay ai-offer-generation-overlay" role="presentation">
        <div
          className="zone-modal ai-offer-generation-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Generating AI offers"
        >
          <div className="ai-offer-generation-orb" aria-hidden="true">
            <LoaderCircle size={34} />
          </div>
          <div className="ai-offer-generation-copy">
            <p className="offers-eyebrow">AI offers</p>
            <h3>Building smart offer drafts</h3>
            <p>Analyzing your store context and preparing merchant-ready recommendations.</p>
          </div>
          <div className="ai-offer-generation-steps" aria-label="Generation progress">
            {GENERATION_STEPS.map((step, index) => (
              <div className="ai-offer-generation-step" key={step}>
                <span>{index + 1}</span>
                <p>{step}</p>
              </div>
            ))}
          </div>
          <div className="ai-offer-generation-bar" aria-hidden="true">
            <span />
          </div>
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <section className="ai-offer-panel" aria-label="AI offer recommendations">
      {publishModal}
      {rulesModal}
      {generationModal}
      <ConfirmDialog
        open={!!duplicateConfirm}
        title="Similar offer already exists"
        description={duplicateConfirm?.message
          || 'A similar offer already exists for this store. Do you still want to publish this one?'}
        confirmLabel="Publish anyway"
        cancelLabel="Cancel"
        confirmVariant="primary"
        busy={isMutating}
        onConfirm={confirmForcePublish}
        onCancel={() => setDuplicateConfirm(null)}
      />
      <div className="ai-offer-panel-header">
        <div className="ai-offer-panel-title-block">
          <p className="offers-eyebrow">AI offers</p>
          <div className="ai-offer-title-row">
            <h3>Smart offer drafts</h3>
            <AiBadge title="AI-powered drafts" />
          </div>
          <p>{hasRecommendations ? `${visibleRecommendations.length} draft${visibleRecommendations.length === 1 ? '' : 's'} ready` : 'No drafts ready'}</p>
        </div>
        <div className="ai-offer-panel-actions">
          <button
            className="btn ghost"
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            aria-label="Refresh AI offer drafts"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>
      </div>

      {generationAlertMessage ? (
        <div className="ai-offer-alert">
          <AlertTriangle size={16} />
          <span>{generationAlertMessage}</span>
        </div>
      ) : null}

      {isInitialLoading ? (
        <div className="offers-cards-grid">
          {Array.from({ length: 2 }).map((_, index) => (
            <article className="offer-card ai-offer-card" key={`ai-offer-skeleton-${index}`}>
              <Skeleton height={16} width="42%" />
              <Skeleton height={24} width="70%" />
              <Skeleton height={14} width="90%" />
              <Skeleton height={86} width="100%" />
            </article>
          ))}
        </div>
      ) : error && !hasRecommendations ? (
        <EmptyState
          icon={<AlertTriangle size={20} />}
          title="Couldn't load AI drafts"
          description="Try refreshing the recommendations."
          action={{ label: 'Retry', onClick: handleRefresh }}
        />
      ) : !hasRecommendations ? (
        <div className="ai-offer-empty-state">
          <p>No smart drafts yet.</p>
          <p className="ai-offer-empty-hint">
            Use <strong>Generate AI Offer</strong> at the top of the page to create your first set.
          </p>
        </div>
      ) : (
        <div className="offers-cards-grid ai-offer-grid">
          {visibleRecommendations.map((recommendation) => (
            <AiOfferRecommendationCard
              key={recommendation.id}
              recommendation={recommendation}
              onPublish={openPublishModal}
              onDismiss={dismissRecommendation}
              publishDisabled={isMutating}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default AiOfferRecommendationPanel;
